/* eslint-disable no-bitwise */
import createDOMPurify from 'dompurify';
import { encode as encodeTags, decode as decodeTags } from 'html-entities';
import moment from 'moment/moment';
import React from 'react';

const DOMPurify = createDOMPurify(window);

const utils = {
  formatDate: (date) => moment.utc(date).format('L'),
  formatDateNoLocale: (date) => moment(date).format('YYYY-MM-DD'),
  formatTimeLocale: (date) => moment(date).local().format('HH:mm'),
  formatDateTime: (date) => moment(date).format('L HH:mm'),
  formatDateTimeLocale: (date) => moment(date).local().format('MM/DD/YYYY HH:mm'),
  formatDateTimeNoLocale: (date) => moment(date).utc().format('MM/DD/YYYY HH:mm'),
  capitalizeString: (string) => {
    string = string.toLowerCase().split(' ');
    for (let i = 0; i < string.length; i++) {
      string[i] = string[i].charAt(0).toUpperCase() + string[i].slice(1);
    }
    return string.join(' ');
  },
  removeUnderscores: (string) => string.split('_').join(' '),
  removeUnderscoresTitleCase: (string) => {
    const s = string.split('_').join(' ').split('-').join(' ').toLowerCase().split(' ').filter(Boolean);
    for (let i = 0; i < s.length; i++) {
      s[i] = s[i][0].toUpperCase() + s[i].slice(1);
    }
    return s.join(' ');
  },
  splitCamelcaseString: (string) => string.replace(/([a-z0-9])([A-Z])/g, '$1 $2'),
  camelToSnake: (str) =>
    str.replace(
      /([a-z][A-Z])/g,
      (group) => `${group.toLowerCase().substring(0, 1)}-${group.toLowerCase().substring(1)}`
    ),
  snakeToCamel: (str, divider = '') =>
    str.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', divider).replace('_', divider)),
  rgbToHex: (a) => {
    a = a.replace(/[^\d,]/g, '').split(',');
    return `#${((1 << 24) + (+a[0] << 16) + (+a[1] << 8) + +a[2]).toString(16).slice(1)}`;
  },
  toUserFriendlySize: (bytes) => {
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    return `${(bytes / 1024 ** i).toFixed(2) * 1} ${sizes[i]}`;
  },
  getPropertyByName: (path, obj, separator = '.') => {
    const properties = Array.isArray(path) ? path : path.split(separator);
    return properties.reduce((prev, curr) => prev && prev[curr], obj);
  },
  flattenKeys: (prefix, obj) =>
    obj &&
    Object.keys(obj).reduce(
      (accum, key) => ({
        ...accum,
        [`${prefix}__${key}`]: obj[key],
      }),
      {}
    ),
  unFlattenKeys: (prefix, obj) =>
    Object.keys(obj)
      .filter((key) => key.startsWith(`${prefix}__`))
      .reduce(
        (accum, key) => ({
          ...accum,
          [key.replace(`${prefix}__`, '')]: obj[key],
        }),
        {}
      ),
  extractField: (list, field) => list.map((i) => i[field]),
  distinctValues: (list) =>
    list.reduce((accum, value) => {
      if (!accum.includes(value)) accum.push(value);
      return accum;
    }, []),
  getId: () => Math.random().toString(36).substr(2, 9),
  merge: (a, b, p) => a.filter((aa) => !b.find((bb) => aa[p] === bb[p])).concat(b),
  getStartDate: (noOfDays) => {
    const d = new Date();
    const date = d.setDate(d.getDate() - noOfDays);
    // Get the time value in milliseconds and convert to seconds
    return (date / 1000) | 0;
  },
  // regex for look behind to support safari browsers
  numberWithCommas(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },
  createIFrameContentData: (html) => `data:text/html,<meta charset="utf-8"/>${html}`,
  renderHTMLTags: (data) => (
    <span
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(data),
      }}
    />
  ),
  findScript: (text) => {
    if (text) {
      // eslint-disable-next-line
      text = text.replace(new RegExp('\r?\n', 'g'), '');
      const decodedValue = utils.decodeTags(text);
      const foundScript = utils.parseJsScript(decodedValue);
      if (foundScript && foundScript[1]) {
        const clearedScript = foundScript[1].replace(/<[^>]*>?/gm, '');
        // execute script only in questionnaire not in outcome builder
        if (clearedScript) {
          return utils.executeScript(clearedScript);
        }
      }
    }
  },
  parseJsScript: (value) => {
    // eslint-disable-next-line
    let foundScript = value.match(/\<script\>(.*)\<\/script\>/);
    if (foundScript && foundScript[1]) {
      const clearedScript = foundScript[1].replace(/<[^>]*>?/gm, '');
      foundScript[1] = clearedScript;
    }
    return foundScript;
  },
  encodeTags,
  decodeTags,
  executeScript: (code = '') => {
    if (code) {
      try {
        const innerScript = code.replace(/<[/]*script>/, '');
        // eslint-disable-next-line no-new-func
        const testScript = new Function(innerScript);
        testScript();
        const script = document.createElement('script');
        script.innerHTML = code;
        script.async = true;
        document.body.appendChild(script);
        return true;
      } catch (e) {
        alert(`Script error: \n${e.message}`);
        return false;
      }
    }
  },
};

export default utils;
